/**
 * ./src/modules/shopping-list/components/ShoppingListFilters.tsx
 *
 * ShoppingListFilters component
 *
 * @copyright DesignShop, 2024
 * @since 08/14/2024
 */
import { Button } from '@/modules/common/components/Button';
import { Skeleton } from '@/modules/common/components/skeleton';
import { useAppDispatch, useAppSelector } from '@/modules/common/hooks/useStore';
import { cn } from '@/modules/common/lib/utils';

import { useShoppingList } from '../context/ShoppingListContext';
import {
  TShoppingListFilters,
  selectCurrentFilter,
  setFilterOption,
} from '../state/shoppingListSlice';

type TShoppingListFilterParameters = {
  slug: TShoppingListFilters;
  label: string;
  quantity: number;
};

export const ShoppingListFilters = () => {
  const dispatch = useAppDispatch();
  const currentFilter = useAppSelector(selectCurrentFilter);
  const { mixedShoppingListItems, isLoading } = useShoppingList();

  const filters: TShoppingListFilterParameters[] = [
    {
      slug: 'all',
      label: 'All',
      quantity: 0,
    },
    {
      slug: 'product',
      label: 'Materials',
      quantity: 0,
    },
    {
      slug: 'collection',
      label: 'Sample the Look',
      quantity: 0,
    },
    {
      slug: 'look',
      label: 'Visualized Rooms',
      quantity: 0,
    },
  ];

  mixedShoppingListItems.reduce((acc, wishlistItem) => {
    const type = acc.find((t) => t.slug === wishlistItem.type);
    if (type) {
      type.quantity += 1;
    }
    const allType = acc.find((t) => t.slug === 'all');
    if (allType) {
      allType.quantity += 1;
    }
    return acc;
  }, filters);

  const handleFilterChange = (type: TShoppingListFilters) => {
    dispatch(setFilterOption(type));
  };

  if (isLoading) {
    return <ShoppingListFiltersSkeleton />;
  }

  return (
    <>
      <div className="flex gap-2 pb-[1.375rem] pt-[.875rem] md:gap-3 md:pb-[1.625rem] md:pt-2">
        {filters.map((type) => {
          const isCurrentFilter = currentFilter === type.slug;

          if (type.quantity === 0) {
            return null;
          }

          return (
            <Button
              variant={isCurrentFilter ? 'primary' : 'secondary'}
              className="py-1.5"
              key={type.label}
              onClick={() => handleFilterChange(type.slug as TShoppingListFilters)}
            >
              <div className="flex items-center gap-1">
                {type.label}

                {type.slug !== 'all' && type.quantity > 0 && (
                  <div className="flex h-6 w-6 items-center justify-center">
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-gray-500 text-[10px] font-bold leading-[.6875rem] text-white">
                      {type.quantity}
                    </span>
                  </div>
                )}
              </div>
            </Button>
          );
        })}
      </div>
    </>
  );
};

export const ShoppingListFiltersSkeleton = ({ className }: { className?: string }) => {
  const skeletons = ['5.0625rem', '3.9375rem', '7.25rem', '7.6875rem'];
  return (
    <div
      className={cn(
        'flex gap-2 pb-[1.375rem] pt-[.875rem] md:gap-3 md:pb-[1.625rem] md:pt-2',
        className
      )}
    >
      <div className="overflow-x-auto">
        <div className="flex gap-3 md:flex-wrap">
          {skeletons.map((width, index) => (
            <Skeleton style={{ width }} className="h-9 shrink-0 rounded-lg" key={index} />
          ))}
        </div>
      </div>
    </div>
  );
};
